/**
 * Shared formatting utilities for ForgeBlocks.
 *
 * Currency, dates, numbers, and tier names — one canonical implementation
 * so formatting stays consistent across the entire frontend.
 */

/**
 * Format a value in **cents** as USD currency: `$X.XX`.
 *
 * @example formatCurrency(1099)  // "$10.99"
 * @example formatCurrency(500)   // "$5.00"
 * @example formatCurrency(0)     // "$0.00"
 */
export function formatCurrency(cents: number): string {
  const dollars = (cents / 100).toFixed(2);
  return `$${dollars}`;
}

/**
 * Format a date string as a *relative* label when the date is less than 24
 * hours ago (e.g. "2h ago"), otherwise fall back to a short absolute format
 * like "Mar 9, 2026".
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  const MS_PER_MINUTE = 60_000;
  const MS_PER_HOUR = 3_600_000;
  const MS_PER_DAY = 86_400_000;

  if (diffMs < 0) {
    // Future date — just show the absolute format.
    return formatAbsoluteShort(date);
  }

  if (diffMs < MS_PER_MINUTE) {
    return "just now";
  }

  if (diffMs < MS_PER_HOUR) {
    const mins = Math.floor(diffMs / MS_PER_MINUTE);
    return `${mins}m ago`;
  }

  if (diffMs < MS_PER_DAY) {
    const hours = Math.floor(diffMs / MS_PER_HOUR);
    return `${hours}h ago`;
  }

  return formatAbsoluteShort(date);
}

/**
 * Format a date string in full form: "Mar 9, 2026, 11:28 AM".
 */
export function formatDateFull(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Format a number for display.
 *
 * - Values under 1 000 000 get comma-separated grouping: `1,234`.
 * - Values >= 1 000 000 get abbreviated: `1.5M`, `2M`, `10.3M`, etc.
 *
 * @example formatNumber(1234)    // "1,234"
 * @example formatNumber(1500000) // "1.5M"
 * @example formatNumber(2000000) // "2M"
 */
export function formatNumber(value: number): string {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    // Drop the decimal if it's a whole number of millions.
    const formatted =
      millions % 1 === 0 ? millions.toString() : millions.toFixed(1);
    // Trim a trailing ".0" that toFixed might produce when rounding.
    return `${formatted.replace(/\.0$/, "")}M`;
  }

  return value.toLocaleString("en-US");
}

/**
 * Map an internal license type slug to a human-readable display label.
 *
 * The canonical display names are "Standard License" and "Exclusive License".
 * A short form (without "License") is also available for compact badges.
 *
 * @example formatLicenseType("standard")        // "Standard License"
 * @example formatLicenseType("exclusive")       // "Exclusive License"
 * @example formatLicenseType("standard", true)  // "Standard"
 * @example formatLicenseType("exclusive", true) // "Exclusive"
 * @example formatLicenseType(null)              // ""
 */
export function formatLicenseType(
  licenseType: string | null | undefined,
  short = false,
): string {
  if (!licenseType) return "";

  const normalized = licenseType.toLowerCase();
  if (normalized === "standard") return short ? "Standard" : "Standard License";
  if (normalized === "exclusive") return short ? "Exclusive" : "Exclusive License";

  // Fallback: capitalise the first letter of whatever value is stored.
  return licenseType.charAt(0).toUpperCase() + licenseType.slice(1);
}

/**
 * Map an internal tier slug to a human-readable display name.
 *
 * Handles `null` / `undefined` gracefully by falling back to `"Free"`.
 *
 * @example formatTierName("creator") // "Creator"
 * @example formatTierName(null)      // "Free"
 */
export function formatTierName(tier: string | null | undefined): string {
  const TIER_NAMES: Record<string, string> = {
    free: "Free",
    creator: "Creator",
    studio: "Studio",
  };

  if (!tier) {
    return "Free";
  }

  return TIER_NAMES[tier.toLowerCase()] ?? tier;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Short absolute date: "Mar 9, 2026" */
function formatAbsoluteShort(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
